/**
 * «نوع همکاری» — یک ستون به‌جای پنج ستونِ پراکنده.
 *
 * قبلاً برای فهمیدنِ اینکه با یک نفر چطور کار می‌کنیم باید پنج ستون را کنار هم
 * می‌گذاشتی: مدل حقوق، بیمه، حضوری، کارمزد دوخت، خارج از حقوق — و باز هم
 * «شغل‌های دیگر» (نگهبانی، سرایداری) جای دیگری بود. حالا همه یک چک‌لیستِ واحد
 * است: هرچه تیک خورده، همان با او حساب می‌شود.
 *
 * ⚠️ اینجا **منبعِ حقیقتِ تازه‌ای ساخته نمی‌شود**. چک‌لیست فقط نمایی از همان
 * ستون‌های موجودِ دیتابیس است و تیک‌زدن هم همان‌ها را می‌نویسد. اگر ستونِ تازه‌ای
 * برای «نوع همکاری» می‌ساختیم، دو منبعِ حقیقت داشتیم که بالاخره از هم واگرا
 * می‌شدند و موتورِ حقوق نمی‌دانست کدام را باور کند.
 *
 * قاعدهٔ «پایه»: `pay_model` در دیتابیس تک‌مقداری است. از v2.51.0 گزینهٔ
 * **«روزمزد» حذف شد** — در این کارخانه «روزمزد» و «حقوق ثابت» یک چیزند: هر دو
 * یک مبلغِ ماهانه‌اند که غیبت و کسرکار از ۳۰اُمِ همان کم می‌شود (همان کاری که
 * صورتحساب‌های واقعی می‌کنند). داشتنِ دو گزینه فقط باعثِ اشتباه می‌شد.
 * پس پایه یا **ثابت** است یا **کارمزدی**، و کارمزد روی ثابت هم سوار می‌شود.
 *
 * v2.104.0 — دو حالتِ تازه (خواستهٔ کاربر ۱۴۰۵/۰۶/۲۲):
 *  • **راننده** به‌تنهایی هم کافی است («راننده‌اند و حقوق ثابت ندارن»): پایه
 *    `pay_model=none` می‌شود و `driver_type=task_based` تا مأموریت‌ها حساب شود؛
 *    اگر مأموریتی نداشت صفر می‌ماند و با ردیفِ اصلاحی دستی اضافه می‌شود.
 *  • **مدیر کارگاه**: نوعِ همکاریِ جدا که فعلاً در هزینهٔ حقوق نمی‌آید (مثلِ
 *    «خارج از حقوق» رفتار می‌کند) و حقوقِ ثابت لازم ندارد.
 */

export const COLLAB_GROUPS = [
  {
    key: "base",
    label: "پایهٔ حقوق",
    hint: "«کارمزد دوخت» می‌تواند روی «حقوق ثابت» هم سوار شود.",
    items: [
      { key: "fixed", label: "حقوق ثابت ماهانه", help: "مبلغِ ماهانه در فیلدِ «حقوق ثابت» تعیین می‌شود." },
      { key: "piece", label: "کارمزد دوخت", help: "مزدِ دوختی که در تولید برایش ثبت می‌شود — برای روزهایی که «بازهٔ تکی» ندارند. اگر فقط چند روز کارمزدی است، تیک نزنید و در «نقش‌ها و بازه‌ها» بازهٔ کارمزدی بدهید (بازه همیشه مقدم است)." },
      { key: "manager", label: "مدیر کارگاه", help: "فعلاً در هزینهٔ حقوق نمی‌آید و حقوقِ ثابت لازم ندارد؛ فقط نوعِ همکاری‌اش مشخص می‌شود." },
    ],
  },
  {
    key: "extra",
    label: "موارد دیگر",
    items: [
      { key: "insurance", label: "بیمه تأمین اجتماعی" },
      { key: "attendance", label: "حضور و غیاب دستگاه", help: "اگر تیک نخورد، غیبت و کسری برایش حساب نمی‌شود." },
      { key: "driver", label: "راننده", help: "به‌تنهایی هم کافی است: بدونِ حقوقِ ثابت، فقط مأموریت‌ها (بار، تخلیه…) حساب می‌شود — یا صفر می‌ماند و دستی اضافه می‌شود." },
    ],
  },
  {
    key: "special",
    label: "استثنا",
    items: [
      { key: "excluded", label: "خارج از محاسبهٔ حقوق", help: "مثلِ پرسنلِ «کارگاه»: در تولید انتخاب می‌شود تا بالانس خالی نماند، ولی هیچ فیشی نمی‌گیرد. با تیک‌خوردنِ این، بقیه بی‌اثر می‌شوند." },
    ],
  },
];

export const COLLAB_ITEMS = COLLAB_GROUPS.flatMap((g) => g.items);
const LABEL = Object.fromEntries(COLLAB_ITEMS.map((i) => [i.key, i.label]));

const TAG_COLOR = {
  fixed: "blue", piece: "gold", manager: "volcano",
  insurance: "cyan", attendance: "geekblue", driver: "purple",
  excluded: "red",
};

/** کلیدهای تیک‌خوردهٔ یک پرسنل — از همان ستون‌های موجود. */
export function checkedOf(emp) {
  if (!emp) return new Set();
  const s = new Set();
  if (emp.payroll_excluded) s.add("excluded");
  if (emp.is_workshop_manager) s.add("manager");
  // `none` = بدونِ پایه (رانندهٔ بی‌حقوقِ ثابت / مدیر کارگاه): نه ثابت، نه کارمزد
  // «روزمزد» حذف شده؛ دادهٔ قدیمی هم «ثابت» خوانده می‌شود
  if (emp.pay_model === "fixed" || emp.pay_model === "daily") s.add("fixed");
  // کارمزد: یا پایه‌اش کارمزدی است، یا صریحاً روشن شده. «صریحاً خاموش» همیشه نه.
  if (emp.piece_pay_enabled === true) s.add("piece");
  else if (emp.piece_pay_enabled !== false && emp.pay_model === "piece") s.add("piece");
  if (emp.has_insurance) s.add("insurance");
  if (emp.attendance_required) s.add("attendance");
  if (emp.is_driver) s.add("driver");
  return s;
}

/** برچسب‌های نمایشی — شاملِ «شغل‌های دیگر» که فقط-خواندنی‌اند. */
export function tagsOf(emp) {
  const checked = checkedOf(emp);
  if (checked.has("excluded")) {
    // بقیه بی‌اثرند؛ نشان‌دادنشان گمراه‌کننده است
    return [{ key: "excluded", label: LABEL.excluded, color: TAG_COLOR.excluded }];
  }
  const out = COLLAB_ITEMS
    .filter((i) => checked.has(i.key))
    .map((i) => ({ key: i.key, label: i.label, color: TAG_COLOR[i.key] }));
  (emp?.secondary_roles || []).forEach((r) => out.push(
    { key: `role:${r}`, label: r, color: "magenta", secondary: true }));
  return out;
}

/**
 * چک‌لیست → تغییراتی که باید PATCH شود.
 * `prev` برای این است که وقتی «پایه» عوض نشده، `pay_model` بی‌دلیل نوشته نشود.
 */
export function patchFor(checked, prev = {}) {
  const has = (k) => checked.has(k);
  const patch = {
    has_insurance: has("insurance"),
    attendance_required: has("attendance"),
    is_driver: has("driver"),
    payroll_excluded: has("excluded"),
    is_workshop_manager: has("manager"),
  };

  // پایهٔ حقوق
  if (has("fixed")) patch.pay_model = "fixed";
  else if (has("piece")) patch.pay_model = "piece";
  else if (has("driver") || has("manager")) patch.pay_model = "none";   // بدونِ پایه
  else patch.pay_model = prev.pay_model || "fixed";   // بدونِ پایه معنی ندارد

  // نوعِ راننده: بدونِ حقوقِ ثابت ⇒ مأموریتی (مأموریت‌ها حساب می‌شود). با حقوقِ
  // ثابت، مقدارِ قبلی می‌ماند (رانندهٔ ثابتِ ایمپورت‌شده task_based نمی‌شود).
  if (has("driver")) {
    patch.driver_type = has("fixed") ? (prev.driver_type || "fixed") : "task_based";
  } else {
    patch.driver_type = null;
  }

  // کارمزد: روی پایهٔ غیرکارمزدی «صریحاً بله»، روی پایهٔ کارمزدی «طبقِ نوع»
  if (patch.pay_model === "piece") {
    patch.piece_pay_enabled = has("piece") ? null : false;
  } else {
    patch.piece_pay_enabled = has("piece") ? true : false;
  }
  return patch;
}

/** ناسازگاری‌ها — پیش از ذخیره به کاربر گفته می‌شود. */
export function conflictOf(checked) {
  if (checked.has("manager") && ["fixed", "piece"].some((k) => checked.has(k))) {
    return "«مدیر کارگاه» فعلاً در محاسبهٔ حقوق نیست؛ با حقوق ثابت یا کارمزد هم‌زمان تیک نخورد.";
  }
  if (!checked.has("excluded")
      && !["fixed", "piece", "driver", "manager"].some((k) => checked.has(k))) {
    return "دستِ‌کم یک نوع همکاری (حقوق ثابت، کارمزد، راننده یا مدیر کارگاه) باید تیک بخورد.";
  }
  return null;
}

/** انتخابِ یک کلید. (ناسازگاریِ ثابت ⇄ روزمزد با حذفِ روزمزد بی‌موضوع شد.) */
export function toggle(checked, key, on) {
  const next = new Set(checked);
  if (on) {
    next.add(key);
  } else {
    next.delete(key);
  }
  return next;
}
